/**
 * useTerminology — the tenant's vertical copy overrides (e.g. a travel tenant
 * calls a "Lead" an "Enquiry"). Presentational only; behavior is unchanged.
 *
 * The result is fetched once and cached module-wide: a tenant's vertical only
 * changes when a SaaS admin changes it, never within a session. Falls back to
 * the generic copy on any error, so the UI always has labels.
 */
import { useEffect, useState } from 'react';
import adminApi, { VerticalContext } from '@/services/admin.api';

const GENERIC: VerticalContext = {
  vertical: 'generic',
  terminology: { lead: { singular: 'Lead', plural: 'Leads' } },
};

let cache: VerticalContext | null = null;
let inflight: Promise<VerticalContext> | null = null;

function load(): Promise<VerticalContext> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = adminApi
      .getVerticalContext()
      .then((ctx) => {
        cache = ctx;
        return ctx;
      })
      .catch(() => GENERIC)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function useTerminology(): VerticalContext {
  const [ctx, setCtx] = useState<VerticalContext>(cache ?? GENERIC);
  useEffect(() => {
    let alive = true;
    load().then((c) => {
      if (alive) setCtx(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  return ctx;
}

export default useTerminology;
